import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInteractOutside } from "react-aria";
import { Popover } from "react-aria-components";
import { useLocation, useNavigate } from "react-router";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { logoutAuthSession } from "@/features/auth/authSessionThunks";
import { useGetBillingOverviewQuery } from "@/features/billing/billingApi";
import { formatBasisPoints } from "@/features/billing/billingFormatters";
import { merryCardMenuCatalog } from "@/features/foundation/cardMenuCatalog";
import { useGetMerchantContextQuery } from "@/features/chat/merchantContextApi";
import { findBindingForContextShop } from "@/features/chat/merchantContextStore";
import { resolveConversationRouteShopBindingId } from "@/features/chat/conversationShopScope";
import { desktopShellPlaceholderSession, desktopShellRoutes } from "@/features/desktop-shell/desktopShellData";
import { resolveMerchantAgencyName } from "@/features/desktop-shell/merchantBrand";
import {
  useGetAccountSettingsQuery,
  useUpdatePreferencesMutation,
} from "@/features/settings/accountSettingsApi";
import {
  activeStoreCleared,
  selectWorkspaceStoreSeedBindingId,
  workspaceStoreSeedSelected,
} from "@/features/settings/activeStoreSlice";
import { readStorePreference, resolveStorePreference } from "@/features/settings/activeStorePreference";
import { executeHelpSupportAction } from "@/features/settings/helpSupportActions";
import { useGetHelpSupportEntriesQuery } from "@/features/settings/helpSupportApi";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import {
  createStoreSystemMenuCatalog,
  storeBindingsRetryActionId,
} from "@/features/settings/storeSystemMenuCatalog";
import { storeCountryIdentity, storePlatformBadge } from "@/features/settings/storeIdentityVisuals";
import { usePrivateAvatarUrl } from "@/features/settings/usePrivateAvatarUrl";
import type {
  CurrencyCode,
  LocaleCode,
} from "@/features/settings/accountSettingsSchemas";
import {
  MerryCardMenu,
  type MerryCardMenuAction,
  type MerryCardMenuCatalog,
} from "@/shared/ui/MerryCardMenu";
import { MerrySystemMenu } from "@/shared/ui/MerrySystemMenu";
import {
  MerryTopNavigation,
  type MerryTopNavigationSession,
} from "@/shared/ui/MerryTopNavigation";
import {
  getMerrySystemMenuPageFigmaNodeId,
} from "@/shared/ui/merryCardMenuVariants";

type OpenSystemMenu = "account" | "store" | null;

interface DesktopTopNavigationProps {
  beforeNavigate?: (navigate: () => void) => void;
}

const helpSupportActionPrefix = "help-support:";
const helpSupportRetryActionId = "help-support-retry";

export function DesktopTopNavigation({ beforeNavigate }: DesktopTopNavigationProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const organizationId = useAppSelector(
    (state) => state.authSession.actor?.activeOrganizationId ?? "",
  );
  const organizationType = useAppSelector(
    (state) => state.authSession.actor?.organizationType,
  );
  const userId = useAppSelector(state => state.authSession.actor?.userId ?? "");
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const storeTriggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuDialogRef = useRef<HTMLDivElement>(null);
  const settingsQuery = useGetAccountSettingsQuery();
  const helpSupportQuery = useGetHelpSupportEntriesQuery();
  const billingOverviewQuery = useGetBillingOverviewQuery(organizationId, {
    skip: !organizationId,
  });
  const settings = settingsQuery.data;
  const avatarUrl = usePrivateAvatarUrl(settings?.user.avatarResourceId);
  const [updatePreferences] = useUpdatePreferencesMutation();
  const [openMenu, setOpenMenu] = useState<OpenSystemMenu>(null);
  const isOutsideMenuTrigger = (element: Node) =>
    !storeTriggerRef.current?.contains(element) &&
    !accountTriggerRef.current?.contains(element);
  // 非模态 Popover 只自动处理焦点离开；空白区域点击也应收起菜单。
  useInteractOutside({
    ref: menuRef,
    isDisabled: openMenu === null,
    onInteractOutside: (event) => {
      if (event.target instanceof Node && isOutsideMenuTrigger(event.target)) setOpenMenu(null);
    },
  });
  useEffect(() => {
    if (openMenu !== null) menuDialogRef.current?.focus({ preventScroll: true });
  }, [openMenu]);
  const workspaceSeedStoreId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const conversationId = conversationIdFromLocation(location);
  const merchantContextQuery = useGetMerchantContextQuery(
    { conversationId, organizationId },
    { skip: !organizationId },
  );
  const storesQuery = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const currency = settings?.preferences.currencyCode ?? "CNY";
  const language = settings?.preferences.locale ?? "zh-CN";
  const helpSupportEntries = helpSupportQuery.data?.entries;
  const helpSupportEntriesByKey = useMemo(
    () =>
      new Map(
        (helpSupportEntries ?? []).map((entry) => [entry.entryKey, entry]),
      ),
    [helpSupportEntries],
  );
  const systemMenuCatalog = useMemo<MerryCardMenuCatalog>(() => {
    const state = helpSupportQuery.isLoading
      ? { kind: "loading" as const, message: "正在读取帮助项目…" }
      : helpSupportQuery.isError || !helpSupportEntries
        ? {
            action: { id: helpSupportRetryActionId, label: "重新加载" },
            kind: "error" as const,
            message: "帮助项目暂时无法读取",
          }
        : helpSupportEntries.length === 0
          ? { kind: "empty" as const, message: "当前暂无可用的帮助项目" }
          : undefined;

    return {
      ...merryCardMenuCatalog,
      support: {
        items: (helpSupportEntries ?? []).map((entry) => ({
          description: entry.description,
          id: `${helpSupportActionPrefix}${entry.entryKey}`,
          label: entry.title,
          trailing:
            entry.indicatorKind === "external" ? "external" : "none",
        })),
        state,
        title: merryCardMenuCatalog.support.title,
      },
    };
  }, [helpSupportEntries, helpSupportQuery.isError, helpSupportQuery.isLoading]);
  const storeMenuCatalog = useMemo(
    () => createStoreSystemMenuCatalog(
      storesQuery.currentData,
      storesQuery.isError
        ? "error"
        : storesQuery.currentData
          ? "ready"
          : "loading",
    ),
    [storesQuery.currentData, storesQuery.isError],
  );
  const activeStore = useMemo(() => {
    const bindings = storesQuery.currentData?.bindings ?? [];
    if (conversationId) {
      const frozenBinding = findBindingForContextShop(
        bindings,
        merchantContextQuery.currentData?.selectedShop ?? null,
      );
      if (frozenBinding) return frozenBinding;
      const routeBindingId = resolveConversationRouteShopBindingId(
        location.search,
        workspaceSeedStoreId,
        bindings,
      );
      return routeBindingId === null
        ? undefined
        : bindings.find((binding) => binding.id === routeBindingId);
    }
    return (
      bindings.find((binding) => binding.id === workspaceSeedStoreId) ??
      (bindings.length === 1 ? bindings[0] : undefined)
    );
  }, [
    conversationId,
    location.search,
    merchantContextQuery.currentData?.selectedShop,
    storesQuery.currentData?.bindings,
    workspaceSeedStoreId,
  ]);

  useEffect(() => {
    if (!organizationId || !userId || !storesQuery.isSuccess || storesQuery.isError || !storesQuery.currentData) return;
    const saved = workspaceSeedStoreId || readStorePreference(userId, organizationId);
    const next = resolveStorePreference(saved, storesQuery.currentData.bindings.map(binding => binding.id));
    if (next && next !== workspaceSeedStoreId) dispatch(workspaceStoreSeedSelected({ organizationId, storeBindingId: next }));
    else if (!next && saved) dispatch(activeStoreCleared({ organizationId }));
  }, [dispatch, organizationId, userId, storesQuery.currentData, storesQuery.isSuccess, storesQuery.isError, workspaceSeedStoreId]);

  const handleSelectedStoreChange = useCallback(
    (storeBindingId: string) => {
      const binding = storesQuery.currentData?.bindings.find(
        (candidate) => candidate.id === storeBindingId,
      );
      if (!binding || !organizationId) return;

      if (!conversationId) {
        if (location.pathname === "/experience-strategies") {
          const nextSearch = new URLSearchParams(location.search);
          nextSearch.set("shop", storeBindingId);
          nextSearch.delete("q");
          nextSearch.delete("dateMode");
          nextSearch.delete("from");
          nextSearch.delete("to");
          navigate(
            { pathname: location.pathname, search: nextSearch.toString() },
          );
        }
        dispatch(workspaceStoreSeedSelected({ organizationId, storeBindingId }));
        setOpenMenu(null);
        // 首页市场、品类和广告户筛选属于旧店铺，切店后交由新店铺概览重新继承。
        if (location.pathname === "/workspace") navigate("/workspace", { replace: true, state: null });
        return;
      }

      // 已有会话的店铺范围不可重绑；这里仅更新下一段对话的 seed 并返回首页。
      dispatch(workspaceStoreSeedSelected({ organizationId, storeBindingId }));
      setOpenMenu(null);
      navigate("/workspace", { state: null });
    },
    [
      conversationId,
      dispatch,
      location.pathname,
      location.search,
      navigate,
      organizationId,
      storesQuery.currentData?.bindings,
    ],
  );

  const toggleMenu = useCallback(
    (nextMenu: Exclude<OpenSystemMenu, null>) => {
      setOpenMenu((currentMenu) =>
        currentMenu === nextMenu ? null : nextMenu,
      );
    },
    [],
  );

  const handleMenuAction = useCallback(
    (action: MerryCardMenuAction) => {
      if (action.targetView) return;

      if (action.id === helpSupportRetryActionId) {
        void helpSupportQuery.refetch();
        return;
      }

      if (action.id === storeBindingsRetryActionId) {
        void storesQuery.refetch();
        return;
      }

      if (action.id.startsWith(helpSupportActionPrefix)) {
        const entry = helpSupportEntriesByKey.get(
          action.id.slice(helpSupportActionPrefix.length),
        );
        if (!entry) return;
        const openEntry = () => executeHelpSupportAction(entry, navigate);
        if (beforeNavigate) beforeNavigate(openEntry);
        else openEntry();
        setOpenMenu(null);
        return;
      }

      if (action.id === "logout") {
        setOpenMenu(null);
        const logout = () => {
          void dispatch(logoutAuthSession())
            .unwrap()
            .catch(() => undefined)
            .finally(() => navigate("/login", { replace: true }));
        };
        if (beforeNavigate) beforeNavigate(logout);
        else logout();
        return;
      }

      const destination = {
        "account-information": "/settings/account",
        "ads-management": "/settings/ads",
        "notification-management": "/settings/notifications",
        "plan-and-topup": "/settings/billing",
        "store-management": "/settings/stores",
        "task-management": "/settings/tasks",
        "team-management": "/settings/team",
      }[action.id];
      if (destination) {
        const go = () => navigate(destination);
        if (beforeNavigate) beforeNavigate(go);
        else go();
      }
      setOpenMenu(null);
    },
    [beforeNavigate, dispatch, helpSupportEntriesByKey, helpSupportQuery, navigate, storesQuery],
  );

  const storeStatus = storesQuery.isError ? "读取失败" : storesQuery.isLoading ? "读取中" : !activeStore
    ? (storesQuery.currentData?.bindings.length ? "请选择店铺" : "未绑定")
    : activeStore.syncStatus === "reauthorization_required" ? "需重新授权"
    : activeStore.syncStatus !== "healthy" ? "需同步"
    : activeStore.riskCount ? "需关注" : activeStore.pendingTodoCount ? "有待办" : "健康";

  const agencyName = resolveMerchantAgencyName(
    merchantContextQuery.currentData,
    organizationId,
    organizationType,
    settings?.activeOrganization.id === organizationId
      ? settings.activeOrganization.name
      : undefined,
  );

  const session: MerryTopNavigationSession = {
    account: {
      ...desktopShellPlaceholderSession.account,
      accessibleLabel: `查看账户积分，当前 ${formatBasisPoints(
        billingOverviewQuery.data?.account?.overallRemainingBasisPoints,
      )}`,
      avatarSource:
        avatarUrl ??
        desktopShellPlaceholderSession.account.avatarSource,
      onPress: () => toggleMenu("account"),
      triggerRef: accountTriggerRef,
      value: formatBasisPoints(
        billingOverviewQuery.data?.account?.overallRemainingBasisPoints,
      ),
    },
    store: {
      ...desktopShellPlaceholderSession.store,
      accessibleLabel: `查看店铺状态，当前${storeStatus}`,
      avatarSources: [
        "",
        activeStore?.provider === "tiktok_shop" ? desktopShellPlaceholderSession.store.avatarSources[1] : "",
        desktopShellPlaceholderSession.store.avatarSources[2],
      ],
      countryCode: activeStore ? storeCountryIdentity(activeStore.region).code : undefined,
      onPress: () => toggleMenu("store"),
      platformBadge: activeStore ? storePlatformBadge(activeStore.provider) : undefined,
      provider: activeStore?.provider,
      triggerRef: storeTriggerRef,
      value: storeStatus,
    },
    type: "authenticated",
  };

  const updateCurrency = useCallback(
    (nextCurrency: string) => {
      if (!settings || nextCurrency === settings.preferences.currencyCode) return;
      void updatePreferences({
        currencyCode: nextCurrency as CurrencyCode,
        expectedRevision: settings.preferences.revision,
      });
    },
    [settings, updatePreferences],
  );

  const updateLanguage = useCallback(
    (nextLanguage: string) => {
      if (!settings || nextLanguage === settings.preferences.locale) return;
      void updatePreferences({
        expectedRevision: settings.preferences.revision,
        locale: nextLanguage as LocaleCode,
      });
    },
    [settings, updatePreferences],
  );

  return (
    <>
      <MerryTopNavigation
        agencyName={agencyName}
        beforeHomeNavigate={beforeNavigate}
        homeTo={desktopShellRoutes.home.path}
        onOpenCustomerService={() => setOpenMenu(null)}
        session={session}
        statusTestIdPrefix="desktop-shell-status"
      />

      <Popover
        className="merry-system-menu-popover"
        ref={menuRef}
        isNonModal
        isOpen={openMenu !== null}
        offset={0}
        onOpenChange={(isOpen) => {
          if (!isOpen) setOpenMenu(null);
        }}
        placement="bottom end"
        // 触发按钮由 toggleMenu 处理，避免外部关闭先清空状态后又被按钮打开。
        shouldCloseOnInteractOutside={isOutsideMenuTrigger}
        shouldFlip
        triggerRef={
          openMenu === "store" ? storeTriggerRef : accountTriggerRef
        }
      >
        <div
          aria-label="顶部系统菜单"
          className="outline-none"
          ref={menuDialogRef}
          role="dialog"
          tabIndex={-1}
        >
          {openMenu === "account" ? (
            <MerrySystemMenu
              catalog={systemMenuCatalog}
              currency={currency}
              data-testid="desktop-system-menu"
              language={language}
              onAction={handleMenuAction}
              onCurrencyChange={updateCurrency}
              onLanguageChange={updateLanguage}
            />
          ) : null}

          {openMenu === "store" ? (
            <div
              data-figma-node-id={getMerrySystemMenuPageFigmaNodeId("store")}
            >
              <MerryCardMenu
                animateEntrance={false}
                aria-label="店铺系统菜单"
                catalog={storeMenuCatalog}
                data-testid="desktop-system-menu"
                onAction={handleMenuAction}
                onSelectedStoreChange={(storeBindingId) =>
                  void handleSelectedStoreChange(storeBindingId)
                }
                selectedStoreId={activeStore?.id ?? ""}
                view="store"
              />
            </div>
          ) : null}
        </div>
      </Popover>
    </>
  );
}

function conversationIdFromLocationState(value: unknown) {
  if (!value || typeof value !== "object") return "";
  const conversationId = (value as Record<string, unknown>).conversationId;
  return typeof conversationId === "string" ? conversationId.trim() : "";
}

function conversationIdFromLocation(location: {
  pathname: string;
  state: unknown;
}) {
  const match = /^\/workspace\/conversations\/([^/]+)(?:\/|$)/u.exec(
    location.pathname,
  );
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return "";
    }
  }
  return conversationIdFromLocationState(location.state);
}
